
import { defineComponent, reactive, watch } from 'vue';
import Icone from '@/core/components/Icone.vue';
import RequisicaoModal from '@/core/components/Modal/RequisicaoModal.vue';
import { EStatusRetornoRequisicao, IRetornoRequisicao } from '@/core/models/IRetornoRequisicao';
import { ITelaOperacao } from '@/core/models/ITelaOperacao';
import { ETipoPermissao } from '@/models/Enumeradores/MeuSistema/Usuarios/ETipoPermissao';
import { EPermissaoDados } from '@/models/Enumeradores/MeuSistema/Usuarios/EPermissaoDados';
import { useTelaBase } from '@/core/composables/TelaBase';
import { useModalBase } from '@/core/composables/ModalBase';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import { ITipoProducao, ITipoProducaoEmpresa, ITipoProducaoEtapa } from '@/models/Entidades/Cadastros/PCP/ITipoProducao';
import ServicoTipoProducao from '@/servicos/Cadastros/PCP/ServicoTipoProducao';
import { ECustomRenderRow } from '@/core/models/AntDesign/IColumn';
import { useGradeBase } from '@/core/composables/GradeBase';
import Card from '@/core/components/Tela/Card.vue';
import SelecionarEtapaProducao from '@/components/Cadastros/PCP/SelecionarEtapaProducao.vue';

export default defineComponent({
  name: 'TipoProducaoModal',
  props: {
    visivel: {
      type: Boolean,
    },
    operacao: {
      type: Object as () => ITelaOperacao,
      required: true,
    },
  },
  components: {
    Icone,
    Card,
    RequisicaoModal,
    SelecionarEtapaProducao,
  },
  emits: ['sincronizarRegistro', 'update:operacao', 'update:visivel'],
  setup(props, { emit }) {
    const {
      telaBase, obterPermissoes, preencherEmpresasComEstrategiaPermissaoDados, defineEmpresasSelecionadasCadastroCompartilhado,
      preencherPermissoesDados, filtrarPermissaoDadosUsuarioMultiEmpresas, apresentarMensagemSucesso, verificaConceitoParaApresentarEmpresas,
      apresentarMensagemAlerta,
    } = useTelaBase();
    const {
      modalBase, apresentarRetornoRequisicao, apresentarBarraProgresso, ocultarBarraProgresso,
      defineNovaOperacao, defineTextoPadraoBotoes, sincronizarRegistro,
    } = useModalBase(props, emit);
    const {
      gradeBase,
    } = useGradeBase();
    const servicoTipoProducao = new ServicoTipoProducao();
    telaBase.identificadorRecurso = 'CADASTRO_TIPO_PRODUCAO';
    telaBase.identificadorPermissao = 'PER_CADASTRO_TIPO_PRODUCAO';

    const state = reactive({
      tipoProducao: {} as ITipoProducao,
      editavel: true,
      etapa: 0,
      etapaSelecionada: -1,
      descricaoEtapa: '',
      ordem: 0,
    });

    function objetoInicial() {
      state.tipoProducao = {} as ITipoProducao;
      state.tipoProducao.codigo = 0;
      state.tipoProducao.ativo = true;
      state.tipoProducao.apontamentoAutomatico = true;
      state.tipoProducao.empresas = [] as ITipoProducaoEmpresa[];
      state.tipoProducao.etapas = [] as ITipoProducaoEtapa[];
      telaBase.empresasSelecionadas = [];
      state.editavel = true;
      state.etapa = 0;
      state.etapaSelecionada = -1;
      state.descricaoEtapa = '';
    }

    function preencherColunas() {
      gradeBase.colunas = [
        {
          title: 'Código', dataIndex: 'codigo', key: 'codigo', position: 0, visible: false,
        },
        {
          title: 'Ordem', dataIndex: 'ordem', key: 'ordem', position: 1, visible: true, width: 80, align: 'center',
        },
        {
          title: 'Descrição', dataIndex: 'descricao', key: 'Descricao', position: 2, visible: true,
        },
        {
          title: 'Açoes', key: 'acoes', position: 3, visible: true, fixed: 'right', width: 80, align: 'center', customRenderRow: ECustomRenderRow.IconeAcoes,
        },
      ];
    }

    function preparaPersistenciaEmpresas() {
      const tipoProducaoEmpresas : ITipoProducaoEmpresa[] = [];
      if (telaBase.empresasSelecionadas.length > 0) {
        telaBase.empresasSelecionadas.forEach((codigoEmpresa) => {
          const empresaExistente = state.tipoProducao.empresas.find((c) => c.codigoEmpresa === codigoEmpresa);
          if (empresaExistente !== undefined) {
            tipoProducaoEmpresas.push(empresaExistente);
          } else {
            const tipoProducaoEmpresa: ITipoProducaoEmpresa = { codigo: 0, codigoTipoProducao: state.tipoProducao.codigo, codigoEmpresa };
            tipoProducaoEmpresas.push(tipoProducaoEmpresa);
          }
        });
      }
      state.tipoProducao.empresas = tipoProducaoEmpresas;
    }

    async function salvar(salvarNovo: boolean) {
      if (state.tipoProducao.etapas.length === 0) {
        apresentarMensagemAlerta('É necessário informar pelo menos uma etapa!');
        return;
      }

      let retorno: IRetornoRequisicao = { codigoRegistro: 0, status: 0, mensagem: '' };

      apresentarBarraProgresso();
      preparaPersistenciaEmpresas();
      if (props.operacao.tipoPermissaoDados === EPermissaoDados.Incluir) {
        retorno = await servicoTipoProducao.incluir(state.tipoProducao);
      } else if (props.operacao.tipoPermissaoDados === EPermissaoDados.Visualizar) {
        retorno = await servicoTipoProducao.alterar(state.tipoProducao);
      }

      ocultarBarraProgresso();

      if (retorno.status === EStatusRetornoRequisicao.Sucesso) {
        if (props.operacao.tipoPermissaoDados === EPermissaoDados.Incluir) {
          sincronizarRegistro(EPermissaoDados.Incluir, retorno.codigoRegistro);
        } else {
          sincronizarRegistro(EPermissaoDados.Alterar, props.operacao.codigoRegistro);
        }
        apresentarMensagemSucesso(retorno.mensagem);
        if (salvarNovo) {
          objetoInicial();
          telaBase.empresasSelecionadas = [props.operacao.empresaSelecionada];
          defineNovaOperacao(props.operacao);
        } else {
          modalBase.computedVisivel = false;
        }
      } else {
        apresentarRetornoRequisicao(retorno);
      }
    }

    function excluir(index: number) {
      state.tipoProducao.etapas.splice(index, 1);
      state.etapaSelecionada = -1;
    }

    function editar(index: number) {
      state.etapa = state.tipoProducao.etapas[index].codigoEtapa;
      state.ordem = state.tipoProducao.etapas[index].ordem;
      state.etapaSelecionada = index;
    }

    function adicionar() {
      if (!UtilitarioGeral.validaCodigo(state.etapa)) {
        apresentarMensagemAlerta('Por favor selecione uma etapa!');
        return;
      }

      if (UtilitarioGeral.validaLista(state.tipoProducao.etapas)) {
        const codigoEtapa = state.tipoProducao.etapas.findIndex((e) => e.codigoEtapa === state.etapa);
        if (codigoEtapa !== state.etapaSelecionada) {
          if (state.tipoProducao.etapas.some((e) => e.codigoEtapa === state.etapa)) {
            apresentarMensagemAlerta('A etapa já foi adicionada!');
            return;
          }
        }
      }

      if (state.etapaSelecionada > -1) {
        state.tipoProducao.etapas[state.etapaSelecionada].codigoEtapa = state.etapa;
        state.tipoProducao.etapas[state.etapaSelecionada].descricao = state.descricaoEtapa;
        state.tipoProducao.etapas[state.etapaSelecionada].ordem = state.ordem;
      } else {
        const etapaUtilizada: ITipoProducaoEtapa = {} as ITipoProducaoEtapa;
        etapaUtilizada.codigoEtapa = state.etapa;
        etapaUtilizada.descricao = state.descricaoEtapa;
        etapaUtilizada.ordem = state.tipoProducao.etapas.length + 1;
        state.tipoProducao.etapas.push(etapaUtilizada);
      }
      state.etapa = 0;
      state.etapaSelecionada = -1;
      state.descricaoEtapa = '';
      state.ordem = 0;
    }

    watch(async () => modalBase.computedVisivel, async () => {
      telaBase.carregando = true;
      objetoInicial();
      preencherColunas();
      if (modalBase.computedVisivel) {
        if (props.operacao.listaPermissoesDados.length > 0) {
          await preencherPermissoesDados(props.operacao.listaPermissoesDados);
        } else {
          await obterPermissoes(ETipoPermissao.Dados);
        }
        await preencherEmpresasComEstrategiaPermissaoDados(props.operacao.tipoPermissaoDados, true);

        defineTextoPadraoBotoes(props.operacao.tipoPermissaoDados);
        if (props.operacao.tipoPermissaoDados === EPermissaoDados.Visualizar) {
          state.tipoProducao = await servicoTipoProducao.obter(props.operacao.codigoRegistro, props.operacao.empresaSelecionada);
          if (UtilitarioGeral.validaLista(state.tipoProducao.empresas)) {
            state.tipoProducao.empresas.forEach((e) => {
              telaBase.empresasSelecionadas.push(e.codigoEmpresa);
            });
          }
        } else {
          await defineEmpresasSelecionadasCadastroCompartilhado(props.operacao.empresaSelecionada);
        }

        telaBase.permissaoDados = await filtrarPermissaoDadosUsuarioMultiEmpresas(telaBase.empresasSelecionadas);
        verificaConceitoParaApresentarEmpresas();
      }
      telaBase.carregando = false;
    });

    return {
      telaBase,
      props,
      modalBase,
      state,
      salvar,
      objetoInicial,
      EPermissaoDados,
      gradeBase,
      preencherColunas,
      ECustomRenderRow,
      excluir,
      editar,
      adicionar,
    };
  },
});
